import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { NotificationsProvider } from "@/context/NotificationsContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Leads from "@/pages/Leads";
import LeadDetail from "@/pages/LeadDetail";
import Pipeline from "@/pages/Pipeline";
import Activities from "@/pages/Activities";
import Performance from "@/pages/Performance";
import Forecasts from "@/pages/Forecasts";
import Fleet from "@/pages/Fleet";
import Equipment from "@/pages/Equipment";
import ServiceBook from "@/pages/ServiceBook";
import VisioAI from "@/pages/VisioAI";
import Projects from "@/pages/Projects";
import ProjectDetail from "@/pages/ProjectDetail";
import Users from "@/pages/Users";
import Workflow from "@/pages/Workflow";
import Finance from "@/pages/Finance";
import Exec from "@/pages/Exec";
import Inventory from "@/pages/Inventory";
import PrintProject from "@/pages/PrintProject";
import CompanionInfo from "@/pages/CompanionInfo";
import Manual from "@/pages/Manual";
import Audit from "@/pages/Audit";
import Archive from "@/pages/Archive";
import ProductionPositions from "@/pages/ProductionPositions";
import Departments from "@/pages/Departments";
import DepartmentHub from "@/pages/DepartmentHub";
import Leaves from "@/pages/Leaves";
import Coordonare from "@/pages/Coordonare";
import DocumentSearch from "@/pages/DocumentSearch";
import DocTypesAdmin from "@/pages/DocTypesAdmin";
import DocDownload from "@/pages/DocDownload";

function Protected({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading)
    return (
      <div className="flex min-h-screen items-center justify-center bg-background text-muted-foreground">
        Se încarcă…
      </div>
    );
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  return children;
}

function RequireAdmin({ children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

function Home() {
  return <Dashboard />;
}

function App() {
  return (
    <div className="App grain">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/print/project/:id" element={<PrintProject />} />
            <Route
              path="/"
              element={
                <Protected>
                  <NotificationsProvider>
                    <Layout />
                  </NotificationsProvider>
                </Protected>
              }
            >
              <Route index element={<Home />} />
              <Route path="d/:fileId" element={<DocDownload />} />
              <Route path="leads" element={<Leads />} />
              <Route path="leads/:id" element={<LeadDetail />} />
              <Route path="pipeline" element={<Pipeline />} />
              <Route path="activities" element={<Activities />} />
              <Route path="previziuni" element={<Forecasts />} />
              <Route path="parc-auto" element={<Fleet />} />
              <Route path="echipamente" element={<Equipment />} />
              <Route path="carnet-service" element={<ServiceBook />} />
              <Route path="gestiune-terti" element={<Navigate to="/inventory" replace />} />
              <Route path="visio-ai" element={<VisioAI />} />
              <Route path="performance" element={<Performance />} />
              <Route path="projects" element={<Projects />} />
              <Route path="cautare-documente" element={<DocumentSearch />} />
              <Route path="nomenclatoare" element={<DocTypesAdmin />} />
              <Route path="projects/:id" element={<ProjectDetail />} />
              <Route path="coordonare" element={<Coordonare />} />
              <Route path="workflow" element={<RequireAdmin><Workflow /></RequireAdmin>} />
              <Route path="finance" element={<Finance />} />
              <Route path="exec" element={<Exec />} />
              <Route path="inventory" element={<Inventory />} />
              <Route path="companion" element={<CompanionInfo />} />
              <Route path="manual" element={<Manual />} />
              <Route path="audit" element={<Audit />} />
              <Route path="arhiva" element={<Archive />} />
              <Route path="productie" element={<ProductionPositions />} />
              <Route path="departamente" element={<Departments />} />
              <Route path="departamente/:dept" element={<DepartmentHub />} />
              <Route path="concedii" element={<Leaves />} />
              <Route path="users" element={<Users />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" theme="dark" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
